import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { m } from "framer-motion";
import {
  registerHotkey,
  setHotkeysScope,
} from "~/utils/hotkeys";
import { RemoveScroll } from "react-remove-scroll";
import clsx from "clsx";

export function useOutsideRefsClick(
  refs: React.RefObject<HTMLElement>[],
  callback: () => void,
) {
  useEffect(() => {
    // client-side only
    if (typeof document === "undefined") {
      return;
    }
    function listener(e: Event) {
      const filteredRefs = refs.filter(
        (x) =>
          !x.current ||
          x.current.contains(e.target as Node),
      );

      if (filteredRefs.length === 0) {
        callback();
      }
    }

    document.addEventListener("click", listener);

    return () => {
      document.removeEventListener(
        "click",
        listener,
      );
    };
  }, [refs, callback]);
}

export default function Modal(
  props: React.PropsWithChildren<{
    title?: string;
    noSize?: boolean;
    isDisabled?: boolean;
    onDismiss: () => void;
  }>,
) {
  const modalRef = useRef<HTMLDivElement>(null);

  const { onDismiss, isDisabled } = props;
  const [clientSide, setClientSide] =
    useState(false);
  const handleOutsideClickOrDismissKeypress =
    useCallback(() => {
      if (!isDisabled) {
        onDismiss();
      }
    }, [onDismiss, isDisabled]);

  useOutsideRefsClick(
    [modalRef],
    handleOutsideClickOrDismissKeypress,
  );

  useEffect(() => {
    setHotkeysScope("modal");
    if (typeof window !== "undefined") {
      setClientSide(true);
    }
  }, []);
  useEffect(() => {
    const { unbind } = registerHotkey(
      "Esc",
      "modal",
      (e: KeyboardEvent) => {
        e.preventDefault();
        handleOutsideClickOrDismissKeypress();
      },
    );

    return () => {
      unbind();
    };
  }, [handleOutsideClickOrDismissKeypress]);
  if (!clientSide) return null;
  return (
    <>
      {createPortal(
        <>
          {/* Overlay */}
          <RemoveScroll>
            <div className="fixed inset-0 z-30">
              <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.75 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="absolute inset-0 bg-zinc-700"
              />
            </div>

            {/* Modal */}
            <m.div
              initial={{
                opacity: 0,
                scale: 0.95,
              }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-40 flex items-center justify-center"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
            >
              <div
                ref={modalRef}
                className={clsx(
                  !props.noSize && "w-full",
                  !props.noSize &&
                    "max-md:w-350 sm:max-w-lg",
                  "z-50  overflow-hidden rounded bg-white text-zinc-900 shadow-md",
                )}
              >
                <div className="flex items-center border-b px-5 py-4">
                  <h3
                    className={clsx(
                      "flex-1 text-15 font-bold leading-6 tracking-tight",
                      "text-center",
                      "py-5",
                    )}
                    id="modal-title"
                  >
                    {props.title}
                  </h3>
                </div>
                {/* <div className={clsx("overflow-scroll", "h-400")}>
                </div> */}
                {props.children}
              </div>
            </m.div>
          </RemoveScroll>
        </>,
        document.getElementById(
          "modal-container",
        ) as Element,
      )}
    </>
  );
}
